// public/v3/src/lib/draft.js — accessors for the per-patient draft used by Studio.
//
// Care plans and billing notes live in the `care_plans` table (server route
// /data/careplans), not on the patient blob. Each row is keyed by
// (patient_id, month, type) and stores the plan as `plan_text` (plain string
// — either the AI-generated narrative or a manually written one).
//
// GET /data/careplans returns a structured response:
//   { care_plan: { pid: { month: text } },
//     billing_notes: { pid: { month: { bn_code: text } } } }
//
// The legacy `getDraft(patient, month)` (kept below for back-compat) only
// looked at patient._raw.carePlan / billingNote — those fields don't exist
// in the v1 data model, so it always returned the stub defaults. Studio
// should use `useDraft(patient, month)` instead, which fetches care plans
// once and filters locally.
//
// Program partitioning (CCM vs BHI):
//   - The cache holds the full /data/careplans payload separately per
//     program (`_cacheByProgram`), so invalidating one program doesn't
//     nuke the other.
//   - The care-plan narrative (`care_plan` slot) is unified per
//     (patient, month) — both programs read the same comprehensive
//     narrative, by design.
//   - Billing notes (`billing_notes` slot) are filtered per program by
//     the type-key convention from lib/draft-helpers.js: keys prefixed
//     'bhi_' are BHI; everything else is CCM. `useDraft(patient, month,
//     refreshKey, 'bhi')` only surfaces bhi_* notes; the default 'ccm'
//     call only surfaces non-bhi notes (bn_combined, bn_99490, …).

import { useState, useEffect } from 'react';
import { ccmApi } from './api';
import { entriesForMonth } from './activity-log';
import { filterBnMapByProgram } from './draft-helpers';

const EMPTY_DRAFT = Object.freeze({
  careplan: { goals: [], interventions: [], ai: '' },
  billing:  { cpt: '', totalMin: 0, targetMin: 0, activities: 0, revenue: 0, summary: '', hl: [] },
  timelog:  [],
});

// Module-level cache so flipping between patients in Studio doesn't hit the
// network every time. Care plans for the whole panel come back in one call.
//
// The cache is partitioned by program so CCM and BHI drafts for the same
// (patient, month) don't collide. `_cacheByProgram` stores the full
// per-program fetch result. We keep the fetch all-at-once (server returns
// the whole panel) but track CCM and BHI as separate top-level slots so
// invalidation can target one without nuking the other. Filtering of the
// billing-notes map happens in `useDraft` via `filterBnMapByProgram`.
const _cacheByProgram = new Map();   // program → { care_plan, billing_notes }
const _inflightByProgram = new Map();// program → shared Promise while fetching

function fetchAll(program) {
  const prog = program || 'ccm';
  const cached = _cacheByProgram.get(prog);
  if (cached) return Promise.resolve(cached);
  const inflight = _inflightByProgram.get(prog);
  if (inflight) return inflight;
  // CCM and BHI both currently read from /data/careplans (same server route).
  // The `type` column on the care_plans table is what distinguishes care_plan
  // rows from bn_* billing-note rows; program separation will be added when
  // the server learns about it (Phase 2+). For now, BHI's slot mirrors CCM's
  // fetched payload — keep the API surface stable so consumers can already
  // pass `program` without behavior drift.
  const fetch = ccmApi.listCarePlans()
    .then((data) => {
      // Server returns { care_plan: {...}, billing_notes: {...} } when the
      // type column is present. Pre-migration fallback: a flat
      // { pid: { month: text } } object — treat that as care_plan only.
      const normalized = (data && (data.care_plan || data.billing_notes))
        ? { care_plan: data.care_plan || {}, billing_notes: data.billing_notes || {} }
        : { care_plan: data || {}, billing_notes: {} };
      _cacheByProgram.set(prog, normalized);
      _inflightByProgram.delete(prog);
      return normalized;
    })
    .catch((e) => {
      _inflightByProgram.delete(prog);
      // Cache an empty result so we don't hammer the endpoint on transient errors.
      const empty = { care_plan: {}, billing_notes: {} };
      _cacheByProgram.set(prog, empty);
      return empty;
    });
  _inflightByProgram.set(prog, fetch);
  return fetch;
}

// Drop cached draft state so subsequent useDraft() calls re-fetch (typically
// after a save). Semantics:
//   invalidateDraftCache()                          → clear all programs
//   invalidateDraftCache(patientId, monthStr)       → clear both CCM and BHI
//                                                     entries for (patient, month)
//   invalidateDraftCache(patientId, monthStr, prog) → clear only `prog`'s
//                                                     entry for (patient, month)
//
// Note: the current cache layer keys by program (not per (patient, month)),
// so the (patientId, monthStr) args are accepted for forward-API stability
// but only the `program` selector affects what gets dropped today.
export function invalidateDraftCache(patientId, monthStr, program) {
  if (patientId == null && monthStr == null && program == null) {
    _cacheByProgram.clear();
    _inflightByProgram.clear();
    return;
  }
  if (program) {
    _cacheByProgram.delete(program);
    _inflightByProgram.delete(program);
  } else {
    _cacheByProgram.delete('ccm');
    _cacheByProgram.delete('bhi');
    _inflightByProgram.delete('ccm');
    _inflightByProgram.delete('bhi');
  }
}

// Combine all bn_* entries for a (patient, month) into a single billing-note
// preview string. The server stores per-CPT-code notes (bn_99490, bn_99439_1,
// etc.); we concatenate them with a blank-line separator so the Studio's
// Billing tab can render a single summary.
function combineBillingNoteText(monthMap) {
  if (!monthMap || typeof monthMap !== 'object') return '';
  return Object.keys(monthMap)
    .sort()
    .map((k) => monthMap[k])
    .filter((t) => typeof t === 'string' && t.trim())
    .join('\n\n');
}

// React hook — returns a draft view for the given patient + YYYY-MM month.
// Care-plan text appears in `careplan.ai`; the existing Studio template
// renders that as the "How this draft was built" panel. `careplan.goals` and
// `.interventions` stay empty arrays because the v1 schema stores narrative
// text, not structured goal/intervention objects.
//
// refreshKey is an opaque value the caller bumps after writing a new plan to
// force a refetch — invalidateDraftCache() should be called immediately
// before bumping it so the next fetch hits the network instead of the cache.
//
// `program` selects which program's draft to surface ('ccm' | 'bhi'); the
// cache partitions by program so CCM and BHI drafts for the same patient/
// month don't collide. Defaults to 'ccm' for backward compatibility with
// existing call sites.
export function useDraft(patient, month, refreshKey = 0, program = 'ccm') {
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  useEffect(() => {
    if (!patient?.id || !month) {
      setDraft(EMPTY_DRAFT);
      return;
    }
    let alive = true;
    // Local time-log filter from the patient blob doesn't need the network.
    // entriesForMonth strips consolidated/synthetic entries so the Studio
    // display + totals don't double-count after a monthly push.
    const tl = entriesForMonth(patient._raw?.activityLog, month);

    fetchAll(program).then((data) => {
      if (!alive) return;
      // care_plan narrative is unified per (patient, month) — same comprehensive
      // plan regardless of program, by design. No program filtering.
      const cpText = data.care_plan?.[patient.id]?.[month] || '';
      // billing_notes are partitioned by type-key prefix: 'bhi_*' for BHI,
      // everything else for CCM. Filter before combining so each program's
      // summary only reflects its own notes.
      const bnAll = data.billing_notes?.[patient.id]?.[month] || {};
      const bnMap = filterBnMapByProgram(bnAll, program);
      const bnKeys = Object.keys(bnMap).sort();
      const bnText = combineBillingNoteText(bnMap);
      setDraft({
        careplan: {
          goals: [],
          interventions: [],
          ai: cpText,
        },
        billing: {
          cpt: '',
          totalMin: 0,
          targetMin: 0,
          activities: 0,
          revenue: 0,
          summary: bnText,
          hl: [],
          noteTypes: bnKeys,
          noteMap: bnMap,
        },
        timelog: tl,
      });
    });

    return () => { alive = false; };
  }, [patient?.id, month, patient?._raw?.activityLog, refreshKey, program]);

  return draft;
}

// Legacy synchronous accessor — kept for any callers still reading
// patient._raw.carePlan / billingNote. The v1 schema never wrote to those
// fields, so this always returns the empty draft; new code should use
// `useDraft()` instead.
export function getDraft(patient, month) {
  const raw = patient?._raw || patient || {};
  const cp = (raw.carePlan && raw.carePlan[month]) || null;
  const bn = (raw.billingNote && raw.billingNote[month]) || null;
  const tl = entriesForMonth(raw.activityLog, month);
  return {
    careplan: cp || { goals: [], interventions: [], ai: '' },
    billing:  bn || { cpt: '', totalMin: 0, targetMin: 0, activities: 0, revenue: 0, summary: '', hl: [] },
    timelog:  tl,
  };
}
